import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { BackendService } from './backend.service';
import { JwtService } from './jwt.service';
import { capabilityCheck } from '../pipes/check-capability.pipe';
import { components } from '../shared/types/api.type';

export type InAppNotification = components['schemas']['InAppNotification'];

@Injectable({ providedIn: 'root' })
export class UserNotificationService {
  private notificationsSubject = new BehaviorSubject<InAppNotification[]>([]);
  private totalSubject = new BehaviorSubject<number>(0);
  private pushSubscriptionCountSubject = new BehaviorSubject<number>(0);
  private readonly notificationsChangedSubject = new Subject<void>();

  readonly notifications$ = this.notificationsSubject.asObservable();
  readonly total$ = this.totalSubject.asObservable();
  readonly pushSubscriptionCount$ = this.pushSubscriptionCountSubject.asObservable();
  /**
   * Emits whenever {@link notifyNotificationsChanged} runs (read, edits, deletes, push, service-worker events, etc.).
   * {@link notifyNotificationsChanged} also reloads the header inbox; subscribe here to refresh other views (e.g. notification center).
   */
  readonly notificationsChanged$ = this.notificationsChangedSubject.asObservable();
  readonly inAppTypeMatIconClasses = 'type-icon material-symbols-outlined';

  get notifications(): InAppNotification[] {
    return this.notificationsSubject.value;
  }

  get unreadCount(): number {
    return this.notificationsSubject.value.filter(n => n.status !== 'read' && n.status !== 'dismissed' && n.status !== 'archived').length;
  }

  get pushSubscriptionCount(): number {
    return this.pushSubscriptionCountSubject.value;
  }

  constructor(
    private readonly backendService: BackendService,
    private readonly ngZone: NgZone,
    private readonly jwtService: JwtService
  ) {
    this.listenForPushMessages();
  }

  private listenForPushMessages(): void {
    navigator.serviceWorker?.addEventListener('message', (event) => {
      if (event.data?.type === 'PUSH_RECEIVED') {
        this.ngZone.run(() => this.notifyNotificationsChanged());
      }
    });
  }

  /**
   * Call after any change that should refresh the header inbox and let listeners (e.g. notification center) reload.
   */
  notifyNotificationsChanged(): void {
    this.notificationsChangedSubject.next();
    this.loadNotifications();
  }

  /**
   * Load notifications from the backend and cache them.
   * Uses getNotificationsForUser which returns all notifications for the current user.
   */
  loadNotifications(): void {
    const caps = this.jwtService.userData?.role?.capabilities ?? [];
    if (!capabilityCheck(caps, 'user.notification.view')) {
      this.notificationsSubject.next([]);
      this.totalSubject.next(0);
      this.pushSubscriptionCountSubject.next(0);
      return;
    }
    this.backendService
      .get<'notification/getNotificationsForUser'>('notifications')
      .subscribe((data) => {
        this.notificationsSubject.next(data.notifications);
        this.totalSubject.next(data.total);
        if (typeof data.pushSubscriptionCount === 'number') {
          this.pushSubscriptionCountSubject.next(data.pushSubscriptionCount);
        }
      });
  }

  /**
   * Marks a notification as read on the server, then triggers a full notification refresh.
   */
  markAsRead(notificationId: number): void {
    this.backendService
      .patch<'notification/markNotificationAsRead'>('notifications/{id}/read', {
        path: { id: notificationId }
      })
      .subscribe(() => this.notifyNotificationsChanged());
  }

  archiveNotification(notificationId: number): Observable<unknown> {
    return this.backendService
      .patch<'notification/archiveNotification'>('notifications/{id}/archive', {
        path: { id: notificationId }
      })
      .pipe(tap(() => this.notifyNotificationsChanged()));
  }

  /**
   * Whether a running process still depends on this notification (SHIP-1875).
   *
   * A notification with `requiresAction` is the inbox entry point of a running process (order,
   * claim, document review, inventory declaration, ...). Taking it out of the inbox would hide the open
   * step and the process would stall unnoticed, so only the process itself may do that - it clears
   * `requiresAction` when it resolves. `processRelevant` is the server's verdict and additionally
   * covers a notification linked to a process that has not been executed yet; it is only absent on
   * responses predating SHIP-1875, where the flag alone decides.
   *
   * The server enforces this in every archive and delete path and answers with
   * `NOTIFICATION_NOT_ARCHIVABLE` / `NOTIFICATION_NOT_DELETABLE`. Hiding the button here is
   * convenience, never the protection.
   */
  isProcessRelevant(notification: Pick<InAppNotification, 'requiresAction' | 'processRelevant'>): boolean {
    return notification.processRelevant ?? notification.requiresAction;
  }

  /** Archiving is only meant for purely informational notifications, see {@link isProcessRelevant}. */
  isArchivable(notification: Pick<InAppNotification, 'requiresAction' | 'processRelevant' | 'status'>): boolean {
    return !this.isProcessRelevant(notification) && notification.status !== 'archived';
  }

  /**
   * Deleting is only meant for purely informational notifications, see {@link isProcessRelevant}.
   * Unlike archiving it is destructive, so an already archived notification stays deletable.
   */
  isDeletable(notification: Pick<InAppNotification, 'requiresAction' | 'processRelevant'>): boolean {
    return !this.isProcessRelevant(notification);
  }

  getTypeIconName(notification: Pick<InAppNotification, 'notificationType' | 'requiresAction'>): string {
    if (notification.notificationType?.icon) return notification.notificationType.icon;

    if (notification.requiresAction) return 'assignment_late';

    switch (notification.notificationType?.key) {
      case 'process_update':
        return 'update';
      case 'task_assigned':
        return 'assignment';
      case 'system':
        return 'info';
      default:
        return 'notifications';
    }
  }

  /**
   * Background for type badges (center, detail) and dropdown icon chip.
   * API color first, then priority theme tokens, then a neutral usable with white foreground.
   */
  getTypeBadgeBackgroundColor(notification: Pick<InAppNotification, 'notificationType'>): string {
    const explicit = notification.notificationType?.color?.trim();
    if (explicit) return explicit;

    switch (notification.notificationType?.priority) {
      case 4:
        return 'var(--mat-sys-error)';
      case 3:
        return 'color-mix(in srgb, hsl(var(--clr-warn)) 65%, black)';
      case 2:
        return 'color-mix(in srgb, hsl(var(--clr-info)) 65%, black)';
      default:
        return '#757575';
    }
  }
}
